// @ts-nocheck
/*
 * Port of session-replay/list.js (328 lines). BYTE-IDENTICAL.
 * Server-paginated session list with date-range + agent + min-duration +
 * status filters (server accepts `min_duration` and `status` query args).
 * v1 has the Analytics link commented out â€” kept commented here too so
 * this port matches v1's shipped UI exactly. Active sessions render a
 * green "Live" badge in place of duration. `chunk_count` gates the Watch
 * button (nothing to replay if the backend has no chunks yet).
 * PrimeReact Dropdown â†’ react-select; PrimeReact DataTable (lazy) â†’
 * react-bootstrap Table + manual Pagination (same swap as agent-activity).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card as ShadCard } from '@/components/ui/card';
import { Button as _ShadButton } from '@/components/ui/button';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
const Card: any = ({ className, children, ...props }: any) => (
  <ShadCard className={className} {...props}>
    {children}
  </ShadCard>
);
Card.Header = ({ className, children, ...props }: any) => (
  <div className={_cx('card-header', className)} {...props}>
    {children}
  </div>
);
Card.Body = ({ className, children, ...props }: any) => (
  <div className={_cx('card-body', className)} {...props}>
    {children}
  </div>
);
Card.Title = ({ as: As = 'h5', className, children, ...props }: any) => (
  <As className={_cx('card-title', className)} {...props}>
    {children}
  </As>
);
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
Form.Check = ({
  type = 'checkbox',
  label,
  id,
  className,
  inline,
  children,
  ...props
}: any) => (
  <div className={_cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
    {children}
  </div>
);
const Badge: any = ({ bg, pill, className, children, ...props }: any) => (
  <span
    className={_cx('badge', bg && `bg-${bg}`, pill && 'rounded-pill', className)}
    {...props}
  >
    {children}
  </span>
);
const Table: any = ({
  bordered,
  striped,
  hover,
  responsive: _r,
  className,
  children,
  ...props
}: any) => (
  <table
    className={_cx(
      'table',
      bordered && 'table-bordered',
      striped && 'table-striped',
      hover && 'table-hover',
      className,
    )}
    {...props}
  >
    {children}
  </table>
);
const Pagination: any = ({ className, children, ...props }: any) => (
  <ul className={_cx('pagination', className)} {...props}>
    {children}
  </ul>
);
const _pagItem = (label: string, glyph: any) => ({
  active,
  disabled,
  onClick,
  children,
  ...rest
}: any) => (
  <li
    className={_cx('page-item', active && 'active', disabled && 'disabled')}
    aria-label={label}
    {...rest}
  >
    <button
      type="button"
      className="page-link"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
    >
      {children ?? glyph}
    </button>
  </li>
);
Pagination.Item = _pagItem('Page', null);
Pagination.First = _pagItem('First', '«');
Pagination.Prev = _pagItem('Previous', '‹');
Pagination.Next = _pagItem('Next', '›');
Pagination.Last = _pagItem('Last', '»');
const Tooltip: any = ({ children, id: _id, ...props }: any) => <span {...props}>{children}</span>;
const OverlayTrigger: any = ({ children, overlay, placement: _p, trigger: _t }: any) => {
  let title = '';
  if (overlay?.props?.children) {
    title =
      typeof overlay.props.children === 'string'
        ? overlay.props.children
        : Array.isArray(overlay.props.children)
          ? overlay.props.children.filter((c: any) => typeof c === 'string').join(' ')
          : '';
  }
  return React.cloneElement(children, { title });
};
import Select from 'react-select';
import moment from 'moment';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { useNavigate } from 'react-router-dom';
import { getApiCall } from '@/service/httpCall';
import {
  API_SESSION_REPLAY_SESSIONS,
  API_AGENTS_LIST,
} from '@/service/url';
import Loader from '@/components/partials/components/Loader';
import { formatDuration } from './replayUtils';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { CalendarDateInput } from '@/components/ui/calendar-inputs';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Play,
  Settings2,
} from 'lucide-react';

const defaultStart = () => moment().startOf('month').format('YYYY-MM-DD');
const defaultEnd = () => moment().format('YYYY-MM-DD');

const PAGE_SIZE = 25;

const DURATION_OPTIONS = [
  { label: 'Any duration', value: null },
  { label: '> 1 minute', value: 60 },
  { label: '> 5 minutes', value: 300 },
  { label: '> 15 minutes', value: 900 },
  { label: '> 30 minutes', value: 1800 },
];

const STATUS_OPTIONS = [
  { label: 'All statuses', value: null },
  { label: 'Active', value: 'active' },
  { label: 'Ended', value: 'ended' },
  { label: 'Closed (stale)', value: 'stale_closed' },
];

function SessionReplayList() {
  const navigate = useNavigate();

  const [startDate, setStartDate] = useState(defaultStart());
  const [endDate, setEndDate] = useState(defaultEnd());
  const [agentId, setAgentId] = useState<any>(null);
  const [minDuration, setMinDuration] = useState<any>(null);
  const [status, setStatus] = useState<any>(null);

  const [agents, setAgents] = useState<any[]>([]);
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const resp: any = await getApiCall(API_AGENTS_LIST, { fields: 'min' });
        const list = Array.isArray(resp) ? resp : resp?.rows || resp?.data || [];
        const opts = list
          .map((a: any) => ({
            label:
              `${a.agent_name || a.name || 'Agent'}` +
              (a.agent_number ? ` (${a.agent_number})` : ''),
            value: a.id,
          }))
          .filter((o: any) => o.value != null);
        setAgents([{ label: 'All Agents', value: null }, ...opts]);
      } catch (e) {
        setAgents([{ label: 'All Agents', value: null }]);
      }
    })();
  }, []);

  const fetchData = useCallback(
    async (targetPage = 1) => {
      if (!startDate || !endDate) {
        toast.error('Select both start and end dates');
        return;
      }
      if (moment(startDate).isAfter(moment(endDate))) {
        toast.error('Start date cannot be after end date');
        return;
      }
      setLoading(true);
      try {
        const params = new URLSearchParams({
          start_date: startDate,
          end_date: endDate,
          page: String(targetPage),
          limit: String(PAGE_SIZE),
        });
        if (agentId) params.append('agent_id', String(agentId));
        if (minDuration) params.append('min_duration', String(minDuration));
        if (status) params.append('status', status);

        const resp: any = await getApiCall(
          `${API_SESSION_REPLAY_SESSIONS}?${params.toString()}`,
          {},
        );
        setRows(Array.isArray(resp?.rows) ? resp.rows : []);
        setTotal(Number(resp?.total) || 0);
        setPage(Number(resp?.page) || targetPage);
      } catch (e: any) {
        toast.error(friendlyApiError(e) || 'Failed to load sessions');
        setRows([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [startDate, endDate, agentId, minDuration, status],
  );

  useEffect(() => {
    fetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, minDuration, status]);

  const selectedAgentOption =
    agents.find((o: any) => o.value === agentId) || (agents.length ? agents[0] : null);
  const selectedDurationOption =
    DURATION_OPTIONS.find((o) => o.value === minDuration) || DURATION_OPTIONS[0];
  const selectedStatusOption =
    STATUS_OPTIONS.find((o) => o.value === status) || STATUS_OPTIONS[0];

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const selectStyles = {
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    control: (base: any, state: any) => ({
      ...base,
      minHeight: 36,
      height: 36,
      fontSize: 12,
      borderRadius: 6,
      border: '1px solid hsl(var(--border) / 0.6)',
      backgroundColor: 'hsl(var(--card))',
      boxShadow: state.isFocused
        ? 'inset 2px 2px 5px rgba(163, 177, 198, 0.35), inset -2px -2px 5px rgba(255, 255, 255, 0.9), 0 0 0 3px hsl(var(--ring) / 0.15)'
        : 'inset 2px 2px 5px rgba(163, 177, 198, 0.35), inset -2px -2px 5px rgba(255, 255, 255, 0.9)',
      '&:hover': { borderColor: 'hsl(var(--border))' },
    }),
    valueContainer: (base: any) => ({ ...base, padding: '0 8px', height: 34 }),
    input: (base: any) => ({ ...base, margin: 0, padding: 0, color: 'hsl(var(--foreground))' }),
    singleValue: (base: any) => ({ ...base, color: 'hsl(var(--foreground))' }),
    placeholder: (base: any) => ({ ...base, color: 'hsl(var(--muted-foreground))' }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base: any) => ({
      ...base,
      padding: 6,
      color: 'hsl(var(--muted-foreground))',
    }),
    menu: (base: any) => ({
      ...base,
      borderRadius: 8,
      border: '1px solid hsl(var(--border) / 0.6)',
      backgroundColor: 'hsl(var(--card))',
      color: 'hsl(var(--foreground))',
      boxShadow:
        '10px 10px 24px rgba(163, 177, 198, 0.4), -10px -10px 24px rgba(255, 255, 255, 0.9)',
      overflow: 'hidden',
    }),
    option: (base: any, state: any) => ({
      ...base,
      fontSize: 12,
      padding: '8px 12px',
      backgroundColor: state.isSelected
        ? 'hsl(var(--primary) / 0.15)'
        : state.isFocused
          ? 'hsl(var(--primary) / 0.08)'
          : 'transparent',
      color: state.isSelected ? 'hsl(var(--primary))' : 'hsl(var(--foreground))',
      cursor: 'pointer',
    }),
  };

  return (
    <ScrollPage>
      <PageHeader
        title="Session Replay"
        description="Recorded agent-side sessions with playback, journeys, and analytics."
        actions={
          <Button
            variant="outline"
            onClick={() => navigate('/session-replay/recording-settings')}
          >
            <Settings2 className="h-4 w-4" />
            Recording Settings
          </Button>
        }
      />
      {/* Filters */}
      <div
        className="mb-3 rounded-xl border border-border/60 bg-card px-3 py-2.5"
        style={{
          boxShadow:
            '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
        }}
      >
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-0.5">
            <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Start Date
            </label>
            <CalendarDateInput
              value={startDate}
              onChange={(e) => setStartDate(e)}
              max={endDate}
              className="input-embossed h-9 w-[150px] rounded-md px-2.5 text-xs"
            />
          </div>
          <div className="space-y-0.5">
            <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              End Date
            </label>
            <CalendarDateInput
              value={endDate}
              onChange={(e) => setEndDate(e)}
              min={startDate}
              max={defaultEnd()}
              className="input-embossed h-9 w-[150px] rounded-md px-2.5 text-xs"
            />
          </div>
          <div className="w-[220px] space-y-0.5">
            <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Agent
            </label>
            <Select
              value={selectedAgentOption}
              options={agents}
              onChange={(opt: any) => setAgentId(opt?.value ?? null)}
              placeholder="All Agents"
              isClearable={false}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              styles={selectStyles}
            />
          </div>
          <div className="w-[170px] space-y-0.5">
            <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Duration
            </label>
            <Select
              value={selectedDurationOption}
              options={DURATION_OPTIONS}
              onChange={(opt: any) => setMinDuration(opt?.value ?? null)}
              placeholder="Any duration"
              isClearable={false}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              styles={selectStyles}
            />
          </div>
          <div className="w-[160px] space-y-0.5">
            <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Status
            </label>
            <Select
              value={selectedStatusOption}
              options={STATUS_OPTIONS}
              onChange={(opt: any) => setStatus(opt?.value ?? null)}
              placeholder="All statuses"
              isClearable={false}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              styles={selectStyles}
            />
          </div>
          <Button variant="primary" size="sm" onClick={() => fetchData(1)} disabled={loading}>
            {loading ? 'Loading…' : 'Apply'}
          </Button>
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border border-border bg-card">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-primary/[0.06]">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">
                      Agent
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">
                      Started
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">
                      Duration
                    </th>
                    <th className="w-24 px-4 py-2.5 text-right text-xs font-semibold text-muted-foreground">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-3 py-10 text-center text-sm text-muted-foreground"
                      >
                        No recorded sessions found for the selected filters.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row: any, idx: number) => {
                      const a = row.agent || {};
                      const isStripe = idx % 2 === 1;
                      const isLive = row.status === 'active';
                      return (
                        <tr
                          key={row.id}
                          className={
                            'border-t border-border/50 transition-colors hover:bg-primary/[0.06] ' +
                            (isStripe ? 'bg-muted/30' : 'bg-card')
                          }
                        >
                          <td className="px-4 py-2.5">
                            <div className="font-medium text-foreground">
                              {a.agent_name || `Agent #${row.agent_id}`}
                            </div>
                            {a.agent_number ? (
                              <div className="text-xs text-muted-foreground">
                                {a.agent_number}
                              </div>
                            ) : null}
                          </td>
                          <td className="px-4 py-2.5 font-mono text-xs text-foreground">
                            {row.started_at
                              ? moment(row.started_at).format('YYYY-MM-DD HH:mm:ss')
                              : '—'}
                          </td>
                          <td className="px-4 py-2.5">
                            {isLive ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300">
                                <span className="inline-flex h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                                Live
                              </span>
                            ) : (
                              <span className="font-mono text-xs tabular-nums text-foreground">
                                {formatDuration(row.duration_seconds)}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <button
                              type="button"
                              disabled={!row.chunk_count}
                              onClick={() =>
                                navigate(`/session-replay/watch/${row.session_id}`)
                              }
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15 disabled:cursor-not-allowed disabled:opacity-40"
                              title={row.chunk_count ? 'Watch replay' : 'No recording chunks available'}
                              aria-label="Watch replay"
                            >
                              <Play className="h-3.5 w-3.5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {total > PAGE_SIZE && (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-border/60 px-1 pb-2 pt-3 text-xs text-muted-foreground">
              <span>
                Page{' '}
                <span className="font-medium text-foreground">{page}</span> of{' '}
                <span className="font-medium text-foreground">{totalPages}</span>
                {' · '}
                <span className="font-medium text-foreground">{total}</span> total
              </span>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchData(1)}
                  disabled={page <= 1}
                  className="gap-1.5"
                  aria-label="First page"
                >
                  <ChevronsLeft className="h-3.5 w-3.5" />
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchData(page - 1)}
                  disabled={page <= 1}
                  className="gap-1.5"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchData(page + 1)}
                  disabled={page >= totalPages}
                  className="gap-1.5"
                  aria-label="Next page"
                >
                  Next
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchData(totalPages)}
                  disabled={page >= totalPages}
                  className="gap-1.5"
                  aria-label="Last page"
                >
                  Last
                  <ChevronsRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </ScrollPage>
  );
}

export default SessionReplayList;
